import React, { useEffect, useMemo, useState } from 'react';
import { useCommandCenter } from '../utils/CommandCenterContext';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import { useLocation } from 'react-router-dom';
import { createTicket, commandCenterTicketUnavailableMessage } from '../../../services/ticketService';
import { getLoginUniqueId } from '../../../helpers/commandCentreRouting';
import { CommandCentreInstance } from '../../../api/instances';
import { Ticket } from '../utils/commandCenterTypes';
import { TASK_TYPE_OPTIONS } from '../utils/taskTypes';
import { applyTicketScope, getTicketStats } from '../utils/ticketFilters';
import '../KPIsBar/index.css';
import OnlineLeftSidebar from '../OnlineLeftSidebar/OnlineLeftSidebar';
import '../TicketDetailsPanel/TicketDetailsModal.css';

interface FormValues {
  task_name: string;
  task_urgency: string;
  task_type: string;
  task_description: string;
  sender: { id: number; type: 'instance' } | null;
  assignee_user_ids: number[];
  instanceId: number;
}

interface AssignableUser {
  id: number;
  first_name?: string;
  last_name?: string;
  email_address?: string;
}

interface KPIsBarProps {
  filterKey: string;
  setFilterKey: (key: string) => void;
  activeTab: string;
  instances?: CommandCentreInstance[];
  onCreateInstance?: () => void;
  supportTicketData?: any[];
  setSupportTicketData?: (data: any[]) => void;
  webMaintenanceStep?: number;
  setWebMaintenanceStep?: (step: number) => void;
  assignableUsers?: AssignableUser[];
  onTicketActivity?: (ticketId: number) => void;
  onTicketCreated?: (ticket: any) => void;
  externalTicketScope?: "mine" | "all";
  setExternalTicketScope?: (scope: "mine" | "all") => void;
  commandCentreAuth?: any;
}

const slugify = (s?: string) =>
  (s || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const getAssignableUserName = (user: AssignableUser) =>
  `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
  user.email_address ||
  `User #${user.id}`;

const getAssignableUserEmail = (user: AssignableUser) =>
  user.email_address || 'No email available';

interface CreateTaskAssigneeManagerProps {
  users: AssignableUser[];
  selectedUserIds: number[];
  onChange: (ids: number[]) => void;
}

const CreateTaskAssigneeManager: React.FC<CreateTaskAssigneeManagerProps> = ({
  users,
  selectedUserIds,
  onChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserIdToAdd, setSelectedUserIdToAdd] = useState('');

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) =>
        getAssignableUserName(a).localeCompare(getAssignableUserName(b), undefined, {
          sensitivity: 'base',
        })
      ),
    [users]
  );

  const eligibleUserIds = useMemo(
    () => new Set(sortedUsers.map((user) => Number(user.id))),
    [sortedUsers]
  );

  useEffect(() => {
    const nextIds = selectedUserIds.filter((id) => eligibleUserIds.has(Number(id)));
    if (nextIds.length !== selectedUserIds.length) {
      onChange(nextIds);
    }
  }, [eligibleUserIds, onChange, selectedUserIds]);

  const selectedUsers = useMemo(
    () =>
      selectedUserIds
        .map((id) => sortedUsers.find((user) => Number(user.id) === Number(id)))
        .filter((user): user is AssignableUser => Boolean(user)),
    [selectedUserIds, sortedUsers]
  );

  const selectedIds = useMemo(
    () => new Set(selectedUserIds.map((id) => Number(id))),
    [selectedUserIds]
  );

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const availableUsers = sortedUsers.filter((user) => {
    if (selectedIds.has(Number(user.id))) return false;
    if (!normalizedSearch) return true;

    const label = `${getAssignableUserName(user)} ${getAssignableUserEmail(user)}`.toLowerCase();
    return label.includes(normalizedSearch);
  });

  const visibleAvailableUsers = normalizedSearch
    ? availableUsers
    : availableUsers.slice(0, 25);

  const handleAddAssignee = () => {
    const userId = Number(selectedUserIdToAdd);
    if (!userId || selectedIds.has(userId)) return;
    if (!eligibleUserIds.has(userId)) return;

    onChange([...selectedUserIds, userId]);
    setSelectedUserIdToAdd('');
    setSearchTerm('');
  };

  const handleRemoveAssignee = (userId: number) => {
    onChange(selectedUserIds.filter((id) => Number(id) !== Number(userId)));
    if (String(userId) === selectedUserIdToAdd) {
      setSelectedUserIdToAdd('');
    }
  };

  return (
    <div className="ticket-assignee-manager create-task-assignee-manager">
      <div className="ticket-assignee-manager-header">
        <h4>
          <i className="fa fa-user-shield" /> Assignee manager
        </h4>
        <span className="ticket-assignee-manager-hint">
          Add or remove people responsible for this task.
        </span>
      </div>

      <div className="ticket-assignee-manager-body">
        <ul className="ticket-assignee-manager-list create-task-assignee-list">
          {selectedUsers.length === 0 ? (
            <li className="ticket-assignee-manager-row empty">
              No assignees selected.
            </li>
          ) : (
            selectedUsers.map((user) => (
              <li key={user.id} className="ticket-assignee-manager-row">
                <div className="ticket-assignee-manager-row-main">
                  <span className="ticket-assignee-manager-name">
                    {getAssignableUserName(user)}
                  </span>
                  <span className="ticket-assignee-manager-email">
                    {getAssignableUserEmail(user)}
                  </span>
                </div>
                <button
                  type="button"
                  className="ticket-assignee-manager-row-btn"
                  onClick={() => handleRemoveAssignee(user.id)}
                >
                  <i className="fa fa-user-times" /> Remove
                </button>
              </li>
            ))
          )}
        </ul>

        <div className="ticket-assignee-manager-add-row create-task-assignee-add-row">
          <label className="ticket-assignee-manager-search-label" htmlFor="create-task-assignee-search">
            Search users
          </label>
          <input
            id="create-task-assignee-search"
            className="ticket-assignee-manager-search"
            type="search"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setSelectedUserIdToAdd('');
            }}
            placeholder="Search by name or email…"
          />
          <select
            aria-label="Select user to add"
            value={selectedUserIdToAdd}
            onChange={(event) => setSelectedUserIdToAdd(event.target.value)}
            className="ticket-assignee-manager-select"
          >
            <option value="">
              {availableUsers.length === 0
                ? 'No users available to add'
                : 'Select user to add…'}
            </option>
            {visibleAvailableUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {getAssignableUserName(user)} ({getAssignableUserEmail(user)})
              </option>
            ))}
          </select>
          {!normalizedSearch && availableUsers.length > visibleAvailableUsers.length ? (
            <span className="ticket-assignee-manager-add-hint">
              Showing the first {visibleAvailableUsers.length} users. Search to narrow the list.
            </span>
          ) : null}
          <button
            type="button"
            className="ticket-assignee-manager-row-btn"
            onClick={handleAddAssignee}
            disabled={!selectedUserIdToAdd}
          >
            <i className="fa fa-user-plus" /> Add
          </button>
        </div>
      </div>
    </div>
  );
};

const instanceTimeFilters = [
  { key: '24h', label: 'Last 24h' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'all', label: 'All time' },
  { key: 'hosting', label: 'With Hosting' },
];

const KPIsBar: React.FC<KPIsBarProps> = ({
  filterKey,
  setFilterKey,
  activeTab,
  instances = [],
  onCreateInstance,
  supportTicketData = [],
  setSupportTicketData = () => {},
  webMaintenanceStep = 1,
  setWebMaintenanceStep = () => {},
  assignableUsers = [],
  onTicketActivity,
  onTicketCreated,
  externalTicketScope,
  setExternalTicketScope,
  commandCentreAuth
}) => {
  const { tickets, fetchTickets, ticketRequestState } = useCommandCenter();
  const location = useLocation();
  const login_unique_id = getLoginUniqueId(location.pathname, location.search) || undefined;

  const [stats, setStats] = useState({
    total: 0,
    minor: 0,
    major: 0,
    emergency: 0,
    open: 0,
    closed: 0,
  });

  const [instanceStats, setInstanceStats] = useState<{ [key: string]: number }>({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [localTicketScope, setLocalTicketScope] = useState<"mine" | "all">("all");

  useEffect(() => {
    setExternalTicketScope?.('all');
  }, [setExternalTicketScope]);

  const ticketScope = externalTicketScope ?? localTicketScope;
  const setTicketScope =
    setExternalTicketScope ?? ((scope: "mine" | "all") => setLocalTicketScope(scope));

  const defaultInstance = useMemo(() => {
    if (!login_unique_id || !instances.length) return null;
    const target = slugify(login_unique_id);
    return instances.find((instance) => slugify(instance.displayName) === target) || null;
  }, [login_unique_id, instances]);

  const scopedTickets = useMemo(() => {
    if (!login_unique_id || !defaultInstance) return tickets;

    return tickets.filter((t: any) => {
      const sender = (t as any).sender;
      if (!sender) return false;
      if (sender.type !== 'instance') return false;
      return Number(sender.id) === Number(defaultInstance.id);
    });
  }, [tickets, login_unique_id, defaultInstance]);

  useEffect(() => {
    if (activeTab === 'Tickets') {
      const source = applyTicketScope(scopedTickets as Ticket[], ticketScope, commandCentreAuth);
      setStats(getTicketStats(source));
    }
  }, [scopedTickets, activeTab, ticketScope, commandCentreAuth]);

  useEffect(() => {
    if (activeTab === 'Businesses') {
      const stats: { [key: string]: number } = {};
      const now = Date.now();

      instanceTimeFilters.forEach((f) => {
        if (f.key === 'hosting') {
          stats[f.key] = instances.filter(
            (b) => b.hosting && Object.keys(b.hosting).length > 0
          ).length;
          return;
        }

        stats[f.key] = instances.filter((b) => {
          const createdAtStr = (b as any).createdAt || (b as any).created_at;
          if (!createdAtStr) return false;
          const createdAt = new Date(String(createdAtStr).replace(' ', 'T')).getTime();
          if (isNaN(createdAt)) return false;

          switch (f.key) {
            case '24h':
              return now - createdAt <= 24 * 60 * 60 * 1000;
            case '7d':
              return now - createdAt <= 7 * 24 * 60 * 60 * 1000;
            case '30d':
              return now - createdAt <= 30 * 24 * 60 * 60 * 1000;
            case 'all':
            default:
              return true;
          }
        }).length;
      });

      setInstanceStats(stats);
    }
  }, [instances, activeTab]);

  const runCreateTask = () => {
    if (ticketRequestState === 'unavailable') {
      alert(commandCenterTicketUnavailableMessage);
      return;
    }
    setShowCreateModal(true);
  };

  const onSubmit = async (values: FormValues, helpers: FormikHelpers<FormValues>) => {
    try {
      await createTicket(values);
      setShowCreateModal(false);
      setWebMaintenanceStep(1);
      await fetchTickets();
    } catch (error: any) {
      const message = error?.message || 'Error creating task.';
      helpers.setSubmitting(false);
      alert(message);
    }
  };

  const initialSender = useMemo(
    () =>
      defaultInstance
        ? { id: defaultInstance.id, type: 'instance' as const }
        : instances.length
        ? { id: instances[0].id, type: 'instance' as const }
        : null,
    [defaultInstance, instances]
  );

  return (
    <div className="kpis-bar">
      <div className="kpi-header">
        {activeTab === 'Tickets' && (
          <>
            <button className="create-task-btn" onClick={runCreateTask} disabled={ticketRequestState === 'unavailable'} title={ticketRequestState === 'unavailable' ? commandCenterTicketUnavailableMessage : undefined}>
              <i className="fa fa-plus" /> Create Task
            </button>

            <div className="ticket-scope-toggle">
              <button
                type="button"
                className={`ticket-scope-btn ${
                  ticketScope === 'mine' ? 'active' : ''
                }`}
                onClick={() => setTicketScope('mine')}
                title="Show tickets assigned to me"
              >
                <i className="fa fa-user" /> <span>My tasks</span>
              </button>
              <button
                type="button"
                className={`ticket-scope-btn ${
                  ticketScope === 'all' ? 'active' : ''
                }`}
                onClick={() => setTicketScope('all')}
                title="Show all tickets"
              >
                <i className="fa fa-list" /> <span>All tasks</span>
              </button>
            </div>
          </>
        )}

        {activeTab === 'Businesses' && onCreateInstance && (
          <button className="create-task-btn" onClick={onCreateInstance}>
            <i className="fa fa-plus" /> Create Business
          </button>
        )}

        {activeTab === 'Teams' && (
          <div className="online-sidebar-wrapper">
            <OnlineLeftSidebar currentUserId="1" />
          </div>
        )}
      </div>

      <div className="kpi-list">
        {activeTab === 'Tickets' &&
          [
            { key: 'all', label: 'Total Tickets', value: stats.total },
            { key: 'minor', label: 'Minor', value: stats.minor },
            { key: 'major', label: 'Major', value: stats.major, color: '#a93737' },
            {
              key: 'emergency',
              label: 'Emergency',
              value: stats.emergency,
              color: '#a93737',
            },
            { key: 'open', label: 'Open', value: stats.open },
            { key: 'closed', label: 'Closed', value: stats.closed },
          ].map((kpi) => (
            <div
              key={kpi.key}
              className={`kpi-item ${filterKey === kpi.key ? 'active' : ''}`}
              onClick={() => setFilterKey(kpi.key)}
              title={`Show ${kpi.label.toLowerCase()} tickets`}
            >
              <h3>{kpi.label}</h3>
              <span style={{ color: kpi.color || undefined }}>{kpi.value}</span>
            </div>
          ))}

        {activeTab === 'Businesses' &&
          instanceTimeFilters.map((f) => (
            <div
              key={f.key}
              className={`kpi-item ${filterKey === f.key ? 'active' : ''}`}
              onClick={() => setFilterKey(f.key)}
            >
              <h3>{f.label}</h3>
              <span>{instanceStats[f.key] || 0}</span>
            </div>
          ))}
      </div>

      {showCreateModal && (
        <div
          className="ticket-modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="ticket-modal-content create-task-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-modal-btn"
              onClick={() => setShowCreateModal(false)}
            >
              <i className="fa fa-times" />
            </button>
            <div className="create-task-modal-header">
              <h2>
                <i className="fa fa-cog"></i> Create New Web Task
              </h2>
              <p>Share the details the team needs to complete this ticket request.</p>
            </div>

            <Formik<FormValues>
              enableReinitialize
              initialValues={{
                task_name: '',
                task_urgency: 'minor',
                task_type: 'change-text-on-my-website',
                task_description: '',
                sender: initialSender,
                assignee_user_ids: [],
                instanceId: initialSender?.id || 0,
              }}
              onSubmit={onSubmit}
            >
              {({ setFieldValue, values }) => (
                <Form className="core-command-dashboard-form">
                  {instances.length > 0 && (
                    <div className="core-command-dashboard-form-row">
                      <div className="core-command-dashboard-form-column-full">
                        <h3>
                          <i className="fa fa-building"></i> Select Business
                        </h3>
                        <Field name="sender">
                          {({ field }: any) => {
                            const isLocked = !!login_unique_id && !!defaultInstance;

                            if (isLocked && defaultInstance) {
                              return (
                                <select {...field} value={defaultInstance.id} disabled>
                                  <option value={defaultInstance.id}>
                                    {defaultInstance.displayName}
                                  </option>
                                </select>
                              );
                            }

                            return (
                              <select
                                {...field}
                                value={field.value?.id || ''}
                                onChange={(e) => {
                                  const selectedInstance = instances.find(
                                    (instance) => instance.id === Number(e.target.value)
                                  );
                                  setFieldValue(
                                    'sender',
                                    selectedInstance
                                      ? { id: selectedInstance.id, type: 'instance' }
                                      : null
                                  );
                                  setFieldValue('instanceId', Number(e.target.value) || undefined);
                                  const eligibleUserIds = new Set(
                                    assignableUsers.map((user) => Number(user.id))
                                  );
                                  setFieldValue(
                                    'assignee_user_ids',
                                    (values.assignee_user_ids || []).filter((id) =>
                                      eligibleUserIds.has(Number(id))
                                    )
                                  );
                                }}
                              >
                                {instances.map((b) => (
                                  <option key={b.id} value={b.id}>
                                    {b.displayName}
                                  </option>
                                ))}
                              </select>
                            );
                          }}
                        </Field>
                      </div>
                    </div>
                  )}

                  <div className="core-command-dashboard-form-row">
                    <div className="core-command-dashboard-form-column-full">
                      <CreateTaskAssigneeManager
                        users={assignableUsers}
                        selectedUserIds={values.assignee_user_ids || []}
                        onChange={(ids) => setFieldValue('assignee_user_ids', ids)}
                      />
                    </div>
                  </div>

                  <div className="core-command-dashboard-form-row">
                    <div className="core-command-dashboard-form-column-one-half">
                      <ErrorMessage name="task_name" component="span" />
                      <h3>
                        <i className="fa fa-list"></i> Task name
                      </h3>
                      <Field
                        type="text"
                        placeholder="Task name"
                        name="task_name"
                        aria-label="Task name"
                      />
                    </div>
                    <div className="core-command-dashboard-form-column-one-half">
                      <h3>
                        <i className="fa fa-bell"></i> Task urgency
                      </h3>
                      <Field as="select" name="task_urgency">
                        <option value="minor">Minor</option>
                        <option value="major">Major</option>
                        <option value="emergency">Emergency</option>
                      </Field>
                    </div>
                  </div>

                  <div className="core-command-dashboard-form-row">
                    <div className="core-command-dashboard-form-column-full">
                      <h3>
                        <i className="fa fa-question"></i> Task type
                      </h3>
                      <Field as="select" name="task_type">
                        {TASK_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Field>
                    </div>
                  </div>

                  <div className="core-command-dashboard-form-row">
                    <div className="core-command-dashboard-form-column-full">
                      <h3 className="describe-task-in-detail">
                        <i className="fa fa-keyboard"></i> Describe the task
                      </h3>
                      <Field
                        as="textarea"
                        name="task_description"
                        placeholder="Describe the task in detail."
                      />
                    </div>
                  </div>

                  <div className="create-task-modal-actions">
                    <button
                      type="button"
                      className="secondary-action"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="primary-action">Create Task</button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIsBar;
